import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import teamicon from "../assets/teamicon.png";
import { showToast } from "../components/ToastNotification";

/* Normaliza texto: quita tildes, convierte a minúsculas */
const normalizeText = (str) =>
  str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const ITEMS_PER_PAGE = 3;

const SelectTeamPopup = ({
  tournament,
  selectedSubcategoryId,
  onClose,
  onTeamRegistered,
}) => {
  const token = useSelector((state) => state.user.token);

  const [teams, setTeams] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTeams, setExpandedTeams] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  const modalRef = useRef(null);

  // DEBOUNCE
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(normalizeText(searchTerm));
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // FETCH PRINCIPAL
  useEffect(() => {
    if (!token) return;

    const fetchTeams = async () => {
      try {
        setLoading(true);
        setError("");
        const url = `${process.env.REACT_APP_API_URL}/api/teams/with-subcategories?page=1&limit=${ITEMS_PER_PAGE}&search=${encodeURIComponent(searchQuery)}`;
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Error al obtener los equipos.");
        const json = await response.json();

        const responseData = Array.isArray(json) ? json : json.data;
        const responsePages = json.pages || 1;

        setTeams(responseData || []);
        setTotalPages(responsePages);
        setPage(1);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, [token, searchQuery]);

  // CARGAR MÁS
  const loadMore = async () => {
    if (page >= totalPages || loadingMore) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const url = `${process.env.REACT_APP_API_URL}/api/teams/with-subcategories?page=${nextPage}&limit=${ITEMS_PER_PAGE}&search=${encodeURIComponent(searchQuery)}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await response.json();

      const responseData = Array.isArray(json) ? json : json.data;
      const responsePages = json.pages || 1;

      setTeams((prev) => [...prev, ...(responseData || [])]);
      setPage(json.page || nextPage);
      setTotalPages(responsePages);
    } catch (err) {
      showToast.error("Error al cargar más equipos.");
    } finally {
      setLoadingMore(false);
    }
  };

  // Cerrar con Escape / clic fuera
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  useEffect(() => {
    const listener = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, [onClose]);

  // MÉTODOS DE SELECCIÓN
  const toggleSelection = (team, category) => {
    const key = `${team.id}-${category?.id || "noCat"}`;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const removeSelection = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const selectAllCategories = (team) => {
    if (!team.categories?.length) return;
    const newKeys = new Set(selectedKeys);
    team.categories.forEach((cat) => {
      newKeys.add(`${team.id}-${cat.id || "noCat"}`);
    });
    setSelectedKeys(newKeys);
  };

  const clearSelection = () => setSelectedKeys(new Set());

  const getSelectedArray = () => {
    return Array.from(selectedKeys).map((key) => {
      const [teamId, catId] = key.split("-");
      const team = teams.find((t) => t.id.toString() === teamId) || {};
      const category = team?.categories?.find(
        (c) => (c.id || "noCat") === catId,
      );
      return {
        key,
        teamId: parseInt(teamId),
        teamName: team?.team_name || "",
        category,
      };
    });
  };

  const handleBatchRegister = async () => {
    const selected = getSelectedArray();
    if (selected.length === 0) {
      showToast.error("No hay equipos seleccionados.");
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const item of selected) {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/tournaments/${tournament.id}/teams/${item.teamId}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              subcategoryId: selectedSubcategoryId,
              teamCategoryId: item.category?.id || null,
            }),
          },
        );
        if (!res.ok) throw new Error();
        successCount++;
        showToast.success(`${item.teamName} registrado`);
      } catch {
        failCount++;
        showToast.error(`Falló: ${item.teamName}`);
      }
    }

    if (failCount === 0) {
      onTeamRegistered();
      onClose();
    } else {
      showToast.error(`Éxito: ${successCount}, fallos: ${failCount}`);
    }
  };

  const selectedPreview = getSelectedArray();

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white w-full max-w-xl max-h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-fade-in-up"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⚽</span>
            <h2 className="text-xl font-bold text-gray-800">
              Seleccionar equipos
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {/* Búsqueda */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Buscar equipo (ej: atletico)..."
              className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Vista previa de seleccionados */}
        {selectedPreview.length > 0 && (
          <div className="px-6 py-3 border-b border-gray-100 bg-gray-50">
            <p className="text-xs font-medium text-gray-500 mb-2">
              Seleccionados:
            </p>
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
              {selectedPreview.map((item) => (
                <div
                  key={item.key}
                  className="flex items-center gap-2 pl-2 pr-1.5 py-1.5 bg-white border border-indigo-200 rounded-full shadow-sm whitespace-nowrap"
                >
                  <img
                    src={
                      teams.find((t) => t.id === item.teamId)?.picture_url ||
                      teamicon
                    }
                    alt=""
                    className="w-5 h-5 rounded-full object-cover"
                  />
                  <span className="text-xs font-medium text-gray-700">
                    {item.teamName}
                    {item.category?.category
                      ? ` - ${item.category.category}`
                      : ""}
                  </span>
                  <button
                    onClick={() => removeSelection(item.key)}
                    className="ml-1 text-gray-400 hover:text-red-500 transition"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lista de equipos */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {loading ? (
            <>
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="animate-pulse flex items-center gap-4 p-4 border rounded-xl"
                >
                  <div className="w-10 h-10 rounded-full bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </>
          ) : error ? (
            <div className="text-center py-8 text-red-500 bg-red-50 rounded-xl">
              {error}
            </div>
          ) : teams.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <span className="text-4xl mb-3 block">🔍</span>
              Sin resultados
            </div>
          ) : (
            <>
              {teams.map((team) => {
                const isExpanded = expandedTeams[team.id];
                const teamCategories = team.categories || [];
                const selectedInTeam = teamCategories.filter((cat) =>
                  selectedKeys.has(`${team.id}-${cat.id || "noCat"}`),
                ).length;

                return (
                  <div
                    key={team.id}
                    className="bg-white border border-gray-200 rounded-xl overflow-hidden transition-shadow hover:shadow-md"
                  >
                    <div
                      onClick={() =>
                        setExpandedTeams((prev) => ({
                          ...prev,
                          [team.id]: !prev[team.id],
                        }))
                      }
                      className="flex items-center p-4 cursor-pointer group"
                    >
                      <img
                        src={team.picture_url || teamicon}
                        alt={team.team_name}
                        className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                      />
                      <div className="ml-3 flex-1">
                        <h3 className="font-semibold text-gray-800 group-hover:text-indigo-700 transition">
                          {team.team_name}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {teamCategories.length} categoría(s)
                          {selectedInTeam > 0 && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {selectedInTeam} seleccionada(s)
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedInTeam > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              selectAllCategories(team);
                            }}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium underline"
                          >
                            Todas
                          </button>
                        )}
                        <svg
                          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>

                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        isExpanded
                          ? "max-h-96 opacity-100"
                          : "max-h-0 opacity-0"
                      }`}
                    >
                      <div className="bg-gray-50 border-t border-gray-100 px-4 py-2">
                        {teamCategories.length === 0 ? (
                          <div className="text-sm text-gray-400 py-3 text-center">
                            Sin categorías disponibles
                          </div>
                        ) : (
                          teamCategories.map((cat) => {
                            const key = `${team.id}-${cat.id || "noCat"}`;
                            const isChecked = selectedKeys.has(key);
                            return (
                              <div
                                key={key}
                                onClick={() => toggleSelection(team, cat)}
                                className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-white transition cursor-pointer group"
                              >
                                <div
                                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${
                                    isChecked
                                      ? "bg-indigo-600 border-indigo-600"
                                      : "border-gray-300 group-hover:border-indigo-400"
                                  }`}
                                >
                                  {isChecked && (
                                    <svg
                                      className="w-3.5 h-3.5 text-white"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={3}
                                        d="M5 13l4 4L19 7"
                                      />
                                    </svg>
                                  )}
                                </div>
                                <span className="text-sm text-gray-700 flex-1">
                                  {team.team_name}
                                  {cat.category ? ` Sub ${cat.category}` : ""}
                                  {cat.group_name ? ` ${cat.group_name}` : ""}
                                </span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {page < totalPages && (
                <div className="flex justify-center pt-2 pb-1">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition disabled:opacity-50"
                  >
                    {loadingMore ? "Cargando..." : "Cargar más equipos"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-6 py-4 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-600">
              {selectedKeys.size} equipo(s) seleccionado(s)
            </span>
            {selectedKeys.size > 0 && (
              <button
                onClick={clearSelection}
                className="text-xs text-red-500 hover:text-red-700 font-medium"
              >
                Limpiar selección
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-100 transition"
            >
              Cancelar
            </button>
            <button
              onClick={handleBatchRegister}
              disabled={selectedKeys.size === 0}
              className={`flex-1 px-4 py-2.5 rounded-xl font-medium text-white transition ${
                selectedKeys.size === 0
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98]"
              }`}
            >
              {selectedKeys.size > 0
                ? `Registrar (${selectedKeys.size})`
                : "Registrar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectTeamPopup;
